import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react"

import {
  isKeyboardControlActivation,
  isKeyboardTypingTarget,
  isModalLayerTarget,
} from "@/lib/isKeyboardTypingTarget"
import { areSingleKeyShortcutsEnabled } from "@/lib/singleKeyShortcuts"
import type { JsonObject } from "@/types/json"

import { DEFAULT_SHORTCUTS_BY_KEY } from "./defaultKeyboardShortcuts"
import {
  areShortcutsEqual,
  areShortcutStatesEqual,
} from "./keyboardShortcutEquality"

export interface ShortcutAction {
  label: string
  action: () => void
  key: string
  state?: JsonObject
}

interface EscapeAction {
  action: () => void
  allowInInputs?: boolean
  label: string
  priority?: number
}

interface RegisteredEscapeAction extends EscapeAction {
  id: string
  order: number
}

interface ShortcutsControllerContextType {
  getActiveEscapeLabel: () => string | null
  registerShortcut: (shortcut: ShortcutAction) => void
  registerEscapeAction: (escapeAction: EscapeAction) => string
  unregisterShortcut: (key: string) => void
  unregisterEscapeAction: (id: string) => void
  getShortcuts: () => Map<string, ShortcutAction>
  getShortcutState: (key: string) => JsonObject | undefined
  updateShortcutState: (key: string, state: JsonObject) => void
}

interface KeyboardShortcutsStore {
  getSnapshot: () => ShortcutsState
  subscribe: (listener: () => void) => () => void
}

const ShortcutsControllerContext =
  createContext<ShortcutsControllerContextType | null>(null)
const ShortcutsStoreContext = createContext<KeyboardShortcutsStore | null>(null)

export interface ShortcutsState {
  shortcuts: Map<string, ShortcutAction>
  lastPressedKey: string | null
}

type ShortcutsAction =
  | { type: "REGISTER_SHORTCUT"; shortcut: ShortcutAction }
  | { type: "UNREGISTER_SHORTCUT"; key: string }
  | { type: "UPDATE_SHORTCUT_STATE"; key: string; state: JsonObject }
  | { type: "SET_LAST_PRESSED_KEY"; key: string | null }

export const CONTROL_OPTION_COMMAND_BACKQUOTE_SHORTCUT = "⌃⌥⌘`"

export function shortcutsReducer(
  state: ShortcutsState,
  action: ShortcutsAction,
): ShortcutsState {
  switch (action.type) {
    case "REGISTER_SHORTCUT": {
      const existingShortcut = state.shortcuts.get(action.shortcut.key)
      if (
        existingShortcut &&
        areShortcutsEqual(existingShortcut, action.shortcut)
      ) {
        return state
      }

      const newShortcuts = new Map(state.shortcuts)
      newShortcuts.set(action.shortcut.key, {
        ...action.shortcut,
        state: action.shortcut.state || {},
      })
      return { ...state, shortcuts: newShortcuts }
    }
    case "UNREGISTER_SHORTCUT": {
      const existingShortcut = state.shortcuts.get(action.key)
      const defaultShortcut = DEFAULT_SHORTCUTS_BY_KEY.get(action.key)

      if (defaultShortcut) {
        // The slot should fall back to the default. If it already holds the
        // default there is nothing to change, so keep the same state object to
        // avoid an unnecessary notify()/re-render (React #185).
        if (
          existingShortcut &&
          areShortcutsEqual(existingShortcut, defaultShortcut)
        ) {
          return state
        }

        const newShortcuts = new Map(state.shortcuts)
        newShortcuts.set(action.key, defaultShortcut)
        return { ...state, shortcuts: newShortcuts }
      }

      // No default: the slot should be removed. If the key is already absent
      // there is nothing to change.
      if (!existingShortcut) {
        return state
      }

      const newShortcuts = new Map(state.shortcuts)
      newShortcuts.delete(action.key)
      return { ...state, shortcuts: newShortcuts }
    }
    case "UPDATE_SHORTCUT_STATE": {
      const newShortcuts = new Map(state.shortcuts)
      const shortcut = newShortcuts.get(action.key)
      if (shortcut) {
        const nextState = { ...shortcut.state, ...action.state }
        if (areShortcutStatesEqual(shortcut.state, nextState)) {
          return state
        }

        newShortcuts.set(action.key, {
          ...shortcut,
          state: nextState,
        })
      }
      return { ...state, shortcuts: newShortcuts }
    }
    case "SET_LAST_PRESSED_KEY": {
      return { ...state, lastPressedKey: action.key }
    }
    default:
      return state
  }
}

export function KeyboardShortcutsProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const initialState: ShortcutsState = {
    shortcuts: new Map(DEFAULT_SHORTCUTS_BY_KEY),
    lastPressedKey: null,
  }

  const stateRef = useRef<ShortcutsState>(initialState)
  const snapshotRef = useRef<ShortcutsState>(initialState)
  const listenersRef = useRef(new Set<() => void>())
  const escapeActionsRef = useRef<RegisteredEscapeAction[]>([])
  const escapeActionOrderRef = useRef(0)
  const shortcutsRef = useRef(initialState.shortcuts)

  const getActiveEscapeAction = useCallback((): EscapeAction | undefined => {
    return [...escapeActionsRef.current].sort((a, b) => {
      const priorityDelta = (b.priority ?? 0) - (a.priority ?? 0)
      if (priorityDelta !== 0) {
        return priorityDelta
      }
      return b.order - a.order
    })[0]
  }, [])

  const notify = useCallback(() => {
    listenersRef.current.forEach((listener) => {
      listener()
    })
  }, [])

  const applyAction = useCallback(
    (action: ShortcutsAction) => {
      const currentState = stateRef.current
      const nextState = shortcutsReducer(currentState, action)

      if (nextState === currentState) {
        return
      }

      stateRef.current = nextState
      snapshotRef.current = nextState
      shortcutsRef.current = nextState.shortcuts
      notify()
    },
    [notify],
  )

  // Create a class to handle keyboard events
  class KeyboardHandler {
    private isMounted = true
    private actionTimeout: NodeJS.Timeout | null = null
    private clearKeyTimeout: NodeJS.Timeout | null = null
    private sequenceTimeout: NodeJS.Timeout | null = null
    private sequenceBuffer: string[] = []
    // Keys held down, so a prefix held through `g t` stays armed for `g h`.
    private heldKeys = new Set<string>()
    private isBufferHeld = false
    private lastActionTime = 0
    private lastActionKey: string | null = null
    private readonly DEBOUNCE_TIME = 100 // milliseconds
    private readonly SEQUENCE_TIMEOUT = 1000 // milliseconds

    constructor(
      private shortcuts: Map<string, ShortcutAction>,
      private dispatch: (action: ShortcutsAction) => void,
      private getActiveEscapeAction: () => EscapeAction | undefined,
    ) {
      window.addEventListener("keydown", this.handleKeyPress, {
        passive: false,
      })
      window.addEventListener("keyup", this.handleKeyUp)
      window.addEventListener("blur", this.handleBlur)
    }

    handleKeyUp = (event: KeyboardEvent) => {
      // macOS drops the keyup of a key released while Command is down.
      if (event.key === "Meta") {
        this.releaseHeldKeys()
        return
      }
      const releasedKey = this.normalizeKey(event)
      this.heldKeys.delete(releasedKey)
      if (this.isBufferHeld && this.sequenceBuffer.includes(releasedKey)) {
        this.clearSequenceBuffer(true)
      }
    }

    releaseHeldKeys = () => {
      this.heldKeys.clear()
      if (this.isBufferHeld) {
        this.clearSequenceBuffer(true)
      }
    }

    handleBlur = () => {
      this.heldKeys.clear()
      if (this.sequenceBuffer.length > 0) {
        this.clearSequenceBuffer(true)
      }
    }

    isHeld(keys: string[]) {
      return keys.length > 0 && keys.every((key) => this.heldKeys.has(key))
    }

    updateShortcuts(newShortcuts: Map<string, ShortcutAction>) {
      if (this.isMounted) {
        this.shortcuts = newShortcuts
      }
    }

    getSequenceShortcutKeys(): string[] {
      return Array.from(this.shortcuts.keys()).filter((key) =>
        key.includes(" "),
      )
    }

    normalizeKey(event: KeyboardEvent): string {
      if (event.code === "Space" || event.key === " ") {
        return "Space"
      }
      const key = event.key ?? ""
      if (key.length === 1) {
        return key.toLowerCase()
      }
      return key
    }

    runShortcut(shortcut: ShortcutAction) {
      const now = Date.now()
      if (
        shortcut.key === this.lastActionKey &&
        now - this.lastActionTime < this.DEBOUNCE_TIME
      ) {
        return
      }

      if (this.actionTimeout) {
        clearTimeout(this.actionTimeout)
        this.actionTimeout = null
      }

      this.dispatch({ type: "SET_LAST_PRESSED_KEY", key: shortcut.key })
      this.lastActionTime = now
      this.lastActionKey = shortcut.key

      this.actionTimeout = setTimeout(() => {
        if (this.isMounted) {
          shortcut.action()
          this.clearLastPressedKeyAfterDelay()
        }
      }, this.DEBOUNCE_TIME)
    }

    clearSequenceBuffer(clearLastPressedKey = false) {
      this.sequenceBuffer = []
      this.isBufferHeld = false
      if (this.sequenceTimeout) {
        clearTimeout(this.sequenceTimeout)
        this.sequenceTimeout = null
      }

      if (clearLastPressedKey && this.isMounted) {
        this.dispatch({ type: "SET_LAST_PRESSED_KEY", key: null })
      }
    }

    armSequenceTimeout() {
      if (this.sequenceTimeout) {
        clearTimeout(this.sequenceTimeout)
      }

      this.sequenceTimeout = setTimeout(() => {
        this.sequenceTimeout = null
        if (this.isHeld(this.sequenceBuffer)) {
          this.isBufferHeld = true
          this.armSequenceTimeout()
          return
        }
        this.clearSequenceBuffer(true)
      }, this.SEQUENCE_TIMEOUT)
    }

    handleKeyPress = (event: KeyboardEvent) => {
      if (!this.isMounted) {
        return
      }

      if (
        event.ctrlKey &&
        event.altKey &&
        event.metaKey &&
        !event.shiftKey &&
        event.code === "Backquote"
      ) {
        const shortcut = this.shortcuts.get(
          CONTROL_OPTION_COMMAND_BACKQUOTE_SHORTCUT,
        )
        if (shortcut) {
          event.preventDefault()
          if (!event.repeat) {
            this.runShortcut(shortcut)
          }
        }
        return
      }

      // Handle Command+K (or Ctrl+K) for menu
      if (
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        !event.shiftKey &&
        event.key.toLowerCase() === "k"
      ) {
        event.preventDefault()
        const menuShortcut = this.shortcuts.get("⌘K")
        if (menuShortcut) {
          this.dispatch({ type: "SET_LAST_PRESSED_KEY", key: "⌘K" })
          menuShortcut.action()
          this.clearLastPressedKeyAfterDelay()
        }
        return
      }

      // Plain-key shortcuts must not shadow browser or operating-system chords.
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return
      }

      const pressedKey = this.normalizeKey(event)
      this.heldKeys.add(pressedKey)
      if (pressedKey === "Escape") {
        const target = event.target as HTMLElement | null
        const isKeyboardSearchInput =
          target instanceof HTMLInputElement &&
          target.dataset.keyboardSearchInput === "true"
        const shouldBlurSearchInput =
          isKeyboardSearchInput && target.value.trim().length === 0

        if (shouldBlurSearchInput) {
          event.preventDefault()
          target.blur()
        }

        const activeEscapeAction = this.getActiveEscapeAction()
        if (!activeEscapeAction) {
          return
        }

        if (
          isKeyboardTypingTarget(target) &&
          activeEscapeAction.allowInInputs === false &&
          !shouldBlurSearchInput
        ) {
          return
        }

        event.preventDefault()
        this.dispatch({ type: "SET_LAST_PRESSED_KEY", key: "Escape" })
        activeEscapeAction.action()
        this.clearLastPressedKeyAfterDelay()
        return
      }

      // Escape and the chords above stay global: a modal layer handles its own
      // Escape, and ⌘K opens the menu from anywhere. Every other page shortcut
      // acts on the page a modal covers, so it stands down inside one.
      if (
        (pressedKey.length === 1 && !areSingleKeyShortcutsEnabled()) ||
        isKeyboardTypingTarget(event.target) ||
        isKeyboardControlActivation(event) ||
        isModalLayerTarget(event.target)
      ) {
        if (this.sequenceBuffer.length > 0) {
          this.clearSequenceBuffer(true)
        }
        return
      }

      const sequenceShortcutKeys = this.getSequenceShortcutKeys()
      const nextSequence = [...this.sequenceBuffer, pressedKey].join(" ")
      const startsSequence = sequenceShortcutKeys.some((shortcutKey) =>
        shortcutKey.startsWith(`${pressedKey} `),
      )
      const hasPendingSequence = this.sequenceBuffer.length > 0
      const matchesSequenceShortcut = this.shortcuts.get(nextSequence)
      const extendsSequence = sequenceShortcutKeys.some((shortcutKey) =>
        shortcutKey.startsWith(`${nextSequence} `),
      )

      // Auto-repeat of a key in the pending sequence never advances it.
      if (
        event.repeat &&
        hasPendingSequence &&
        (this.sequenceBuffer.includes(pressedKey) ||
          matchesSequenceShortcut ||
          extendsSequence)
      ) {
        event.preventDefault()
        return
      }

      if (hasPendingSequence || startsSequence) {
        if (matchesSequenceShortcut || extendsSequence || startsSequence) {
          event.preventDefault()
        }

        if (matchesSequenceShortcut) {
          const prefix = this.sequenceBuffer
          this.clearSequenceBuffer()
          this.runShortcut(matchesSequenceShortcut)
          if (this.isHeld(prefix)) {
            this.sequenceBuffer = prefix
            this.isBufferHeld = true
            this.armSequenceTimeout()
          }
          return
        }

        if (hasPendingSequence && extendsSequence) {
          this.sequenceBuffer = [...this.sequenceBuffer, pressedKey]
          this.dispatch({
            type: "SET_LAST_PRESSED_KEY",
            key: this.sequenceBuffer.join(" "),
          })
          this.armSequenceTimeout()
          return
        }

        if (!hasPendingSequence && startsSequence) {
          this.sequenceBuffer = [pressedKey]
          this.dispatch({ type: "SET_LAST_PRESSED_KEY", key: pressedKey })
          this.armSequenceTimeout()
          return
        }

        if (hasPendingSequence) {
          this.clearSequenceBuffer(true)
          return
        }
      }

      const shortcut = this.shortcuts.get(pressedKey)
      if (shortcut) {
        event.preventDefault()
        this.runShortcut(shortcut)
      }
    }

    clearLastPressedKeyAfterDelay() {
      if (!this.isMounted) {
        return
      }

      if (this.clearKeyTimeout) {
        clearTimeout(this.clearKeyTimeout)
      }

      this.clearKeyTimeout = setTimeout(() => {
        this.clearKeyTimeout = null
        if (this.isMounted) {
          this.dispatch({ type: "SET_LAST_PRESSED_KEY", key: null })
        }
      }, 3000)
    }

    cleanup() {
      this.isMounted = false
      if (this.actionTimeout) {
        clearTimeout(this.actionTimeout)
        this.actionTimeout = null
      }
      if (this.clearKeyTimeout) {
        clearTimeout(this.clearKeyTimeout)
        this.clearKeyTimeout = null
      }
      if (this.sequenceTimeout) {
        clearTimeout(this.sequenceTimeout)
        this.sequenceTimeout = null
      }
      window.removeEventListener("keydown", this.handleKeyPress)
      window.removeEventListener("keyup", this.handleKeyUp)
      window.removeEventListener("blur", this.handleBlur)
    }
  }

  // Initialize the keyboard handler on mount and clean up on unmount
  const keyboardHandlerClassRef = React.useRef(KeyboardHandler)
  const keyboardHandlerRef = React.useRef<KeyboardHandler | null>(null)

  React.useEffect(() => {
    keyboardHandlerRef.current?.cleanup()
    keyboardHandlerRef.current = new keyboardHandlerClassRef.current(
      shortcutsRef.current,
      applyAction,
      getActiveEscapeAction,
    )

    return () => {
      if (keyboardHandlerRef.current) {
        keyboardHandlerRef.current.cleanup()
        keyboardHandlerRef.current = null
      }
    }
  }, [applyAction, getActiveEscapeAction])

  const registerShortcut = useCallback(
    (shortcut: ShortcutAction) => {
      applyAction({ type: "REGISTER_SHORTCUT", shortcut })
      if (keyboardHandlerRef.current) {
        keyboardHandlerRef.current.updateShortcuts(shortcutsRef.current)
      }
    },
    [applyAction],
  )

  const unregisterShortcut = useCallback(
    (key: string) => {
      applyAction({ type: "UNREGISTER_SHORTCUT", key })
      if (keyboardHandlerRef.current) {
        keyboardHandlerRef.current.updateShortcuts(shortcutsRef.current)
      }
    },
    [applyAction],
  )

  const getShortcuts = useCallback(() => {
    return shortcutsRef.current
  }, [])

  const registerEscapeAction = useCallback((escapeAction: EscapeAction) => {
    const id = `escape-${escapeActionOrderRef.current + 1}`
    escapeActionOrderRef.current += 1
    escapeActionsRef.current = [
      ...escapeActionsRef.current,
      {
        ...escapeAction,
        id,
        order: escapeActionOrderRef.current,
      },
    ]
    return id
  }, [])

  const unregisterEscapeAction = useCallback((id: string) => {
    escapeActionsRef.current = escapeActionsRef.current.filter(
      (escapeAction) => escapeAction.id !== id,
    )
  }, [])

  const getShortcutState = useCallback((key: string) => {
    return shortcutsRef.current.get(key)?.state
  }, [])

  const getActiveEscapeLabel = useCallback(() => {
    return getActiveEscapeAction()?.label ?? null
  }, [getActiveEscapeAction])

  const updateShortcutState = useCallback(
    (key: string, state: JsonObject) => {
      applyAction({ type: "UPDATE_SHORTCUT_STATE", key, state })
      if (keyboardHandlerRef.current) {
        keyboardHandlerRef.current.updateShortcuts(shortcutsRef.current)
      }
    },
    [applyAction],
  )

  const controllerValue = useMemo(
    () => ({
      getActiveEscapeLabel,
      registerShortcut,
      registerEscapeAction,
      unregisterShortcut,
      unregisterEscapeAction,
      getShortcuts,
      getShortcutState,
      updateShortcutState,
    }),
    [
      registerShortcut,
      registerEscapeAction,
      unregisterShortcut,
      unregisterEscapeAction,
      getShortcuts,
      getShortcutState,
      getActiveEscapeLabel,
      updateShortcutState,
    ],
  )

  const storeValue = useMemo<KeyboardShortcutsStore>(
    () => ({
      getSnapshot: () => snapshotRef.current,
      subscribe: (listener: () => void) => {
        listenersRef.current.add(listener)

        return () => {
          listenersRef.current.delete(listener)
        }
      },
    }),
    [],
  )

  return (
    <ShortcutsStoreContext.Provider value={storeValue}>
      <ShortcutsControllerContext.Provider value={controllerValue}>
        {children}
      </ShortcutsControllerContext.Provider>
    </ShortcutsStoreContext.Provider>
  )
}

export const useKeyboardShortcuts = () => {
  const controller = useContext(ShortcutsControllerContext)
  const store = useContext(ShortcutsStoreContext)

  if (!controller || !store) {
    throw new Error(
      "useKeyboardShortcuts must be used within a KeyboardShortcutsProvider",
    )
  }

  const snapshot = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot,
  )

  return {
    ...controller,
    lastPressedKey: snapshot.lastPressedKey,
  }
}

export const useKeyboardShortcutsController = () => {
  const controller = useContext(ShortcutsControllerContext)
  if (!controller) {
    throw new Error(
      "useKeyboardShortcutsController must be used within a KeyboardShortcutsProvider",
    )
  }
  return controller
}
